/**
 * @fileoverview Utilities for validating logo text input data
 *
 * This module provides functions for validating logo text form data, specifically:
 * - Validating logo title and description fields
 * - Validating CTA and provider logo selections
 *
 * @module Logo
 */

import { ProviderLogo, TransformationVariant } from '@amzn/fire-tv-campaign-automation-service';
import { getLogoTextConfig, getRequiredForTransformation } from 'src/configs/logoTextInputConfig';
import { ErrorConstants, TEXT_BASED_TITLE_DESCRIPTION_LENGTH } from 'src/constants';
import { LogoTextState } from 'src/types';

/**
 * Validation errors for logo text input
 */
export interface LogoTextValidationErrors {
  logoTitle?: string;
  logoDescription?: string;
  cta?: string;
  providerLogo?: string;
}

/**
 * Validates logo title field
 *
 * @param logoTitle - The logo title to validate
 * @returns Error message if invalid, undefined if valid
 */
const validateLogoTitle = (logoTitle?: string): string | undefined => {
  const trimmedTitle = logoTitle?.trim();
  if (!trimmedTitle) {
    return ErrorConstants.MESSAGES.LOGO_TITLE_EMPTY;
  }
  return undefined;
};

/**
 * Validates CTA field
 *
 * @param cta - The CTA text to validate
 * @param providerLogo - The selected provider logo (affects max length)
 * @returns Error message if invalid, undefined if valid
 */
const validateCta = (cta?: string, providerLogo?: ProviderLogo): string | undefined => {
  if (!cta) {
    return undefined;
  }

  const ctaMaxLength = providerLogo
    ? TEXT_BASED_TITLE_DESCRIPTION_LENGTH.CTA_MAX_LENGTH_WITH_LOGO
    : TEXT_BASED_TITLE_DESCRIPTION_LENGTH.CTA_MAX_LENGTH_NO_LOGO;

  if (cta.length > ctaMaxLength) {
    return providerLogo
      ? `This CTA is too long when used with a provider logo (max ${ctaMaxLength} characters)`
      : `CTA must be ${ctaMaxLength} characters or less`;
  }

  return undefined;
};

/**
 * Validates provider logo field
 *
 * @param providerLogo - The selected provider logo
 * @param cta - The CTA text (provider logo requires a CTA when present)
 * @param isRequired - Whether providerLogo is required for this variant
 * @returns Error message if invalid, undefined if valid
 */
const validateProviderLogo = (
  providerLogo?: ProviderLogo,
  cta?: string,
  isRequired?: boolean,
): string | undefined => {
  if (!providerLogo) {
    return isRequired ? ErrorConstants.MESSAGES.PROVIDER_LOGO_REQUIRED : undefined;
  }
  // Provider logo requires a CTA
  if (!cta) {
    return ErrorConstants.MESSAGES.PROVIDER_LOGO_REQUIRES_CTA;
  }
  return undefined;
};

/**
 * Validates logo text input state
 *
 * @param logoTextState - The logo text state to validate
 * @param variant - The transformation variant to determine which fields to validate
 * @returns Object containing validation errors, empty if all valid
 */
export const validateLogoTextState = (
  logoTextState: LogoTextState,
  variant: TransformationVariant,
): LogoTextValidationErrors => {
  const errors: LogoTextValidationErrors = {};
  const config = getLogoTextConfig(variant);

  if ('logoTitle' in config) {
    errors.logoTitle = validateLogoTitle(logoTextState.logoTitle);
  }

  if ('cta' in config) {
    errors.cta = validateCta(logoTextState.cta, logoTextState.providerLogo);
  }

  if ('providerLogo' in config) {
    // Only enforce "logo requires CTA" when CTA field exists in the variant config
    const ctaForValidation = 'cta' in config ? logoTextState.cta : 'not-applicable';
    const isProviderLogoRequired = getRequiredForTransformation(variant).includes('providerLogo');
    errors.providerLogo = validateProviderLogo(
      logoTextState.providerLogo,
      ctaForValidation,
      isProviderLogoRequired,
    );
  }

  // Remove undefined values
  Object.keys(errors).forEach((key) => {
    if (errors[key as keyof LogoTextValidationErrors] === undefined) {
      delete errors[key as keyof LogoTextValidationErrors];
    }
  });

  return errors;
};
